/*
 * The rep workspace's keyboard model, kept pure so the page and the tests share it. Keys are single
 * letters with no modifier, and none fire while the rep is typing: the draft editor, an answer box
 * and the search field keep every key for themselves. The exceptions are deliberate and few: Shift+A
 * sends, and the bulk selection (docs/2026-09-30-inbox-bulk-selection-shape.md) takes Shift+J/K to
 * extend, Shift+I to mark read, and Cmd/Ctrl+A to tick every loaded row, the keys Gmail, Superhuman
 * and Linear use for the same moves.
 */
export type Shortcut =
  | "next"
  | "previous"
  | "approve"
  | "send"
  | "copy"
  | "reply"
  | "unread"
  | "toggleSelect"
  | "extendNext"
  | "extendPrevious"
  | "selectAll"
  | "markRead"
  | "undo"
  | "back"
  | "search"
  | "help";

type KeyPress = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey?: boolean;
};

/**
 * Whether a key press belongs to what the rep is typing into: a text field, a choice in a form, or
 * editable content. Every page's keys ask this first, so no letter is ever taken from a field.
 */
export function isTyping(
  target: {
    readonly isContentEditable: boolean;
    readonly tagName: string;
    readonly type?: string;
  } | null,
): boolean {
  if (target === null) return false;
  if (target.isContentEditable || ["TEXTAREA", "SELECT"].includes(target.tagName)) return true;
  // A ticked row's checkbox keeps focus after a click; it takes Space, never letters, so the
  // inbox's keys must still work from it (docs/2026-09-30-inbox-bulk-selection-shape.md).
  return target.tagName === "INPUT" && !NOT_TYPED.has(target.type ?? "text");
}

/** Inputs that take no text, so a letter pressed on them is never the rep typing. */
const NOT_TYPED = new Set(["checkbox", "radio", "button", "submit", "reset"]);

export function shortcutFor(press: KeyPress, typing: boolean): Shortcut | null {
  if (typing || press.altKey) return null;
  // The one modified key the app takes, and only outside a field: select all means the list here.
  if (press.metaKey || press.ctrlKey)
    return press.key.toLowerCase() === "a" && press.shiftKey !== true ? "selectAll" : null;
  switch (press.key) {
    case "j":
      return "next";
    case "k":
      return "previous";
    case "a":
      return "approve";
    // Shift+A only, and only with Shift actually held: a Caps Lock "A" must never send an email.
    case "A":
      return press.shiftKey === true ? "send" : null;
    case "c":
      return "copy";
    // Opens the reply box and nothing more: no key reports, discards or sends a reply.
    case "r":
      return "reply";
    case "u":
      return "unread";
    case "x":
      return "toggleSelect";
    case "z":
      return "undo";
    // Shift only when Shift is really held, as with Shift+A: Caps Lock must not extend a selection.
    case "J":
      return press.shiftKey === true ? "extendNext" : null;
    case "K":
      return press.shiftKey === true ? "extendPrevious" : null;
    case "I":
      return press.shiftKey === true ? "markRead" : null;
    case "Escape":
      return "back";
    case "/":
      return "search";
    case "?":
      return "help";
    default:
      return null;
  }
}

/** Where a go-to chord leads: G, then the place's key (LEA-501), as Gmail and Linear do. */
export type GoTo =
  | "inbox"
  | "meeting"
  | "needs_reply"
  | "follow_up"
  | "campaigns"
  | "answers"
  | "settings";

const GO_TO: ReadonlyMap<string, GoTo> = new Map([
  ["i", "inbox"],
  ["m", "meeting"],
  ["n", "needs_reply"],
  ["f", "follow_up"],
  ["c", "campaigns"],
  ["a", "answers"],
  ["s", "settings"],
]);

/** How long after G its second key still counts. */
export const CHORD_WINDOW_MS = 1500;

/** A G waiting for its second key, and when it was pressed; null when none is. */
export type ChordState = { readonly since: number } | null;

/**
 * What a key does to the shell's navigation keys: the chord it leaves pending, the move it makes
 * (a go-to, or `workspaces` for W, which opens the switcher), and whether it is the shell's, so no
 * page also acts on it. Within the window after G the next key always belongs to the chord: a place
 * moves there, anything else cancels, and neither reaches a page's own M, C or A. Pure, so the time
 * is an input.
 */
export type ChordStep = {
  readonly state: ChordState;
  readonly action: GoTo | "workspaces" | null;
  readonly consumed: boolean;
};

export function chordFor(
  state: ChordState,
  press: KeyPress,
  typing: boolean,
  now: number,
): ChordStep {
  if (typing || press.altKey || press.metaKey || press.ctrlKey)
    return { state: null, action: null, consumed: false };
  if (state !== null && now - state.since <= CHORD_WINDOW_MS)
    return { state: null, action: GO_TO.get(press.key) ?? null, consumed: true };
  if (press.key === "g") return { state: { since: now }, action: null, consumed: true };
  if (press.key === "w") return { state: null, action: "workspaces", consumed: true };
  return { state: null, action: null, consumed: false };
}

/**
 * The item J or K moves to: a conversation in the inbox, a reply in a campaign's preview. With
 * nothing open, J starts at the top and K at the bottom.
 */
export function moveSelection<T>(ids: readonly T[], current: T | null, step: 1 | -1): T | null {
  if (ids.length === 0) return null;
  const index = current === null ? -1 : ids.indexOf(current);
  if (index === -1) return (step === 1 ? ids[0] : ids.at(-1)) ?? null;
  return ids[Math.min(Math.max(index + step, 0), ids.length - 1)] ?? null;
}
